import React from "react";
import styled from "@emotion/styled";
import {useTranslation} from "react-i18next";
import {Language} from "modules/enums";
import {SOCIAL_URLS} from "modules/constants";
import {breakpoints} from "assets/theme";
import IconTwitter from "assets/images/icons/twitter.svg";
import IconFacebook from "assets/images/icons/facebook.svg";
import IconInstagram from "assets/images/icons/instagram.svg";
import IconYoutube from "assets/images/icons/youtube.svg";
import IconTiktok from "assets/images/icons/tiktok.svg";
import LogoGS from "assets/images/logoGS.svg";

const SFooter = styled.footer`
	padding: 48px 100px;
	height: 200px;
	background: ${({theme}) => theme.footer.background};
	color: ${({theme}) => theme.footer.color};
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-wrap: wrap;

	p,
	a {
		font-family: ${({theme}) => theme.secondFont};
		font-style: normal;
		font-weight: 500;
		font-size: 16px;
		line-height: 26px;
	}

	@media screen and (max-width: ${breakpoints.medium}) {
		flex-direction: column;
		height: unset;
		padding: 20px 16px;
		gap: 24px;
	}
`;

const LeftBlock = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	align-items: flex-start;
	gap: 12px;

	p {
		&.rights {
			color: ${({theme}) => theme.footer.rightsColor};
		}
	}

	div {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 76px;
	}

	@media screen and (max-width: ${breakpoints.medium}) {
		align-items: center;
		gap: 8px;

		p {
			&.rights {
				width: 100%;
				text-align: center;
			}
		}

		div {
			gap: 32px;
		}
	}
`;
const RightBlock = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	align-items: center;
	gap: 12px;

	p {
		font-weight: 700;
	}
`;

const Socials = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 14px;

	a {
		width: 24px;
		height: 24px;
		display: flex;
		justify-content: center;
		align-items: center;

		img {
			width: 100%;
			height: auto;
		}
	}
`;

const LogoGenius = styled.img`
	width: 100px;
	height: auto;
	margin-top: 20px;

	@media screen and (max-width: ${breakpoints.medium}) {
		margin-top: 0;
	}
`;

const BottomBlock = styled.div`
	width: 100%;
	display: flex;
	justify-content: flex-end;
	align-items: center;

	@media screen and (max-width: ${breakpoints.medium}) {
		justify-content: center;
	}
`;

export const Footer: React.FC = () => {
	const {t, i18n} = useTranslation();
	const language = i18n.language as Language;

	return (
		<SFooter>
			<LeftBlock>
				<p className="rights">{t("footer.text.rights")}</p>
				<div>
					<a href="https://www.cfl.ca/terms-of-use/">{t("footer.text.terms")}</a>
					<a href="https://www.cfl.ca/privacy-policy/">{t("footer.text.privacy")}</a>
				</div>
			</LeftBlock>
			<RightBlock>
				<p>{t("footer.text.follow")}</p>
				<Socials>
					<a
						href={
							SOCIAL_URLS[language]?.twitter ?? SOCIAL_URLS[Language.English].twitter
						}>
						<img src={IconTwitter} alt="CFL Twitter" />
					</a>
					<a
						href={
							SOCIAL_URLS[language]?.facebook ??
							SOCIAL_URLS[Language.English].facebook
						}>
						<img src={IconFacebook} alt="CFL Facebook" />
					</a>
					<a href="https://www.youtube.com/CFL">
						<img src={IconYoutube} alt="CFL Youtube" />
					</a>
					<a href="https://www.instagram.com/cfl/">
						<img src={IconInstagram} alt="CFL Instagram" />
					</a>
					<a href="https://www.tiktok.com/@cfl">
						<img src={IconTiktok} alt="CFL Tiktok" />
					</a>
				</Socials>
			</RightBlock>
			<BottomBlock>
				<a href="https://www.geniussports.com">
					<LogoGenius src={LogoGS} alt="Genius Sports" />
				</a>
			</BottomBlock>
		</SFooter>
	);
};
